/** LiveArt script */
(function (LiveArt) {
    /**
     * Custom element - a quantity input wrapper for validating the input
     * Used in `quantity-input` snippet.
     */
    class LiveartQuantityElement extends HTMLElement {
        constructor() {
            super();
            // select the crucial elements
            this.minusBtn = this.querySelector("[data-subtract-quantit]");
            this.plusBtn = this.querySelector("[data-add-quantit]");
            this.quantityInput = this.querySelector(
                "input[data-quantity-input]"
            );
            // add listeners
            if (this.minusBtn) {
                this.minusBtn.addEventListener(
                    "click",
                    this.updateValue.bind(this)
                );
            }
            if (this.plusBtn) {
                this.plusBtn.addEventListener(
                    "click",
                    this.updateValue.bind(this)
                );
            }
            if (this.quantityInput) {
                this.quantityInput.addEventListener(
                    "change",
                    function (event) {
                        this.validateRange(event);
                        this.validateStep(event);
                        return true;
                    }.bind(this)
                );
            }
        }

        /**
         * Force number input value to stay within min-max range
         */
        validateRange(event) {
            const el = this.quantityInput;
            if (!el || !(el instanceof HTMLInputElement) || !el.value) return;
            // parse range attributes
            const min = parseInt(el.min, 10);
            const max = parseInt(el.max, 10);
            const value = parseInt(el.value, 10);
            // check the minimum of the range
            if (value < min) {
                el.value = min.toString();
            }
            // check the maximum of the range
            if (value > max) {
                el.value = max.toString();
            }
            // update quantity buttons state
            this.setButtonState(this.minusBtn, value <= min);
            this.setButtonState(this.plusBtn, value >= max);
        }

        /**
         * Set button enabled or disabled state by adding custom class.
         * @param {Element?} el button to update
         * @param {boolean} disable if true the disabling class shall be added, otherwise removed
         */
        setButtonState(el, disable) {
            if (!el) return;
            if (disable) {
                el.classList.add("liveart-disabled");
            } else {
                el.classList.remove("liveart-disabled");
            }
        }

        /**
         * Force number input value to respect step value.
         * Round up to the closest value
         */
        validateStep(event) {
            const el = this.quantityInput;
            if (!el || !(el instanceof HTMLInputElement)) return;
            if (!el.value || !el.step) return;

            const step = parseInt(el.step, 10) || 1;
            const value = parseInt(el.value, 10);
            if (value % step === 0) return;

            el.value = (Math.ceil(value / step) * step).toString();
        }

        /**
         * Update quantity input with +/- buttons.
         */
        updateValue(event) {
            const el = this.quantityInput;
            if (!el || !(el instanceof HTMLInputElement) || !el.value) return;
            if (!event.target) return;

            const step = parseInt(el.step, 10) || 1;
            const value = parseInt(el.value, 10) || 1;

            if (event.target == this.minusBtn) {
                el.value = (value - step).toString();
            } else if (event.target == this.plusBtn) {
                el.value = (value + step).toString();
            }

            el.dispatchEvent(new Event("change", { bubbles: true }));
            return true;
        }
    }

    /**
     * Custom element - checkout button wrapper
     * Adds a listener with custom cart validation
     */
    class LiveartCheckoutElement extends HTMLElement {
        constructor() {
            super();
            this.checkoutBtn = this.querySelector('[name="checkout"]');
            if (!this.checkoutBtn) {
                return;
            }

            this.checkoutBtn.addEventListener(
                "click",
                this.checkoutHandler.bind(this)
            );
            this.prepareModal();
        }

        async checkoutHandler(event) {
            if (event.isCustomHandled) return;
            event.stopPropagation();

            // Disable the button during async processing
            const button = event.target;
            button.disabled = true;

            let isCartValid = true;
            try {
                isCartValid = await this.validateCart();
            } catch (error) {
                console.error("Error in upon updating cart:", error);
            } finally {
                // Enable the button after the async task
                button.disabled = false;
            }

            // Stop further propagation if cart is not valid
            if (!isCartValid) return;

            // Re-dispatch event programmatically
            // Mark the event as custom handled to avoid recursive loop
            const newEvent = new event.constructor(event.type, event);
            newEvent.isCustomHandled = true;
            event.target.dispatchEvent(newEvent);
        }

        async getCart() {
            return fetch("cart.js").then((response) => response.json());
        }

        async getInvalidItems(cart) {
            return fetch(
                "https://boosters.apps.liveartdesigner.com/connector/validate-cart",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(cart),
                }
            ).then((response) => response.json());
        }

        async updateCartItems(updates) {
            return fetch("cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updates }),
            })
                .then((response) => response.json())
                .then(() => window.location.reload());
        }

        async validateCart() {
            const cart = await this.getCart();

            // Check liveart items
            let invalidItems = await this.getInvalidItems(cart);

            if (!Object.keys(invalidItems).length) {
                return true;
            }

            // Handle invalid  entities
            // await this.updateCartItems(invalidItems); // forcefully update the quantities

            const items = cart.items
                .filter((x) => !!invalidItems[x.id])
                .map((x) => ({ ...x, min_quantity: invalidItems[x.id] }));
            this.showMessage(items);

            return false;
        }

        showMessage(items) {
            const modalTable = document.querySelector(
                "#liveart-checkout-modal-content tbody"
            );
            if (!modalTable) return;

            const tableData = items
                .map(
                    (item) =>
                        `<tr>
                            <td><img src="${item.image}"/></td>
                            <td>${item.product_title}</td>
                            <td>${item.min_quantity}</td>
                        </tr>`
                )
                .join("");

            modalTable.innerHTML = tableData;
            MicroModal.show("liveart-checkout-modal");
        }

        prepareModal() {
            const subject = document.querySelector("body");
            if (!subject) return;
            if (document.querySelector("#liveart-checkout-modal")) return;

            MicroModal.init();
            subject.insertAdjacentHTML(
                "beforeend",
                `<div id="liveart-checkout-modal" class="liveart-modal" aria-hidden="true">
                    <div tabindex="-1" class="modal__overlay" data-micromodal-close>
                        <div class="modal__container" role="dialog" aria-modal="true" aria-labelledby="liveart-checkout-modal-title" >
                            <header class="modal__header">
                                <h2 id="liveart-checkout-modal-title" class="modal__title">
                                    Please check the following items
                                </h2>
                                <button class="modal__close" aria-label="Close modal" data-micromodal-close></button>
                            </header>
                        
                            <div id="liveart-checkout-modal-content" class="modal__content">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Preview</th>
                                            <th>Product</th>
                                            <th>Minimum Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>`
            );
        }
    }

    /**
    Customize button logic
    Update designer link with selected variant before redirect
    May need customization according to your theme
    Liveart product id is expected to be specified in data-liveart-product-id button attribute
    Selected variant can be provided in the following ways:
      - data-selected-variant-id attribute
      - variant_selector - selector of the master variants input
      - otherwise attempt to get variant from URL query
  */
    LiveArt.redirectToDesigner = function (event, variant_selector) {
        if (event.preventDefault) event.preventDefault();
        // 1. get data from target attributes
        var designerUrl = event.currentTarget.dataset.liveartDesignerUrl;
        var liveartProductId = event.currentTarget.dataset.liveartProductId;
        var liveartTemplateId = event.currentTarget.dataset.liveartTemplateId;
        var liveartDesignId = event.currentTarget.dataset.liveartDesignId;
        var variant = event.currentTarget.dataset.selectedVariantId;
        // 2. Fallback to get variant from selector
        if (!variant) {
            var variantEl = document.querySelector(variant_selector + "");
            if (variantEl && variantEl.tagName === "SELECT")
                variant = variantEl.selectedOptions[0].value || variant;
            else if (variantEl && variantEl.tagName === "INPUT") {
                variant = variantEl.value || variant;
            } else {
                variant = new URLSearchParams(window.location.search).get(
                    "variant"
                );
            }
        }
        // 3. Form designer url, fallback to default page
        var url = new URL(designerUrl, window.location.href);
        if (!designerUrl) {
            url.pathname = "/pages/liveart-designer";
        }
        // 3.1 Form designer parameters
        var searchParams = new URLSearchParams();
        if (liveartProductId) searchParams.set("product_id", liveartProductId);
        if (liveartTemplateId)
            searchParams.set("template_id", liveartTemplateId);
        if (liveartDesignId) searchParams.set("design_id", liveartDesignId);
        if (variant) searchParams.set("ext_variant_id", variant + "");
        url.search = searchParams.toString();
        // 4. Redirect to designer page
        window.location.href = url.toString();
    };

    function init() {
        document.addEventListener("DOMContentLoaded", function () {
            customElements.define("liveart-quantity", LiveartQuantityElement);
            customElements.define("liveart-checkout", LiveartCheckoutElement);
        });
    }

    init();
})((window.LiveArt = window.LiveArt || {}));
